// Módulo 5: calendario semanal. Se arma solo con las fechas de publicación y las fechas límite.
import * as React from "react";
import { Link } from "@tanstack/react-router";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Boton, cn, Esqueleto, TituloPagina, Vacio } from "@iavmx/ui";
import { useCalendario, useConSesion } from "./consultas";
import { EncabezadoSeccion, Nota, Sem } from "./componentes";
import { diaMes, fechaCorta, sumarDias, ETIQUETA_TAREA, type EstadoTarea } from "./util";

/** Los chips de tareas vencidas antes de la semana traen el día real en que vencieron. */
function vencioEl(ch: { vencioEl?: string }): string | undefined {
  return ch.vencioEl;
}

export function Calendario() {
  const conSesion = useConSesion();
  const [semana, setSemana] = React.useState<string | undefined>(undefined);
  const q = useCalendario(semana);
  if (!conSesion) return <Vacio titulo="Entra para ver el calendario" />;
  if (q.isLoading) return <Esqueleto className="h-80" />;
  if (q.error || !q.data) return <Vacio titulo="No se pudo cargar" texto={q.error?.message} />;
  const d = q.data;
  const rango = `${diaMes(d.lunes)} – ${diaMes(d.domingo)} de ${new Date(`${d.domingo}T12:00:00`).getFullYear()}`;
  const numeroSemana = Math.ceil((new Date(`${d.lunes}T12:00:00Z`).getTime() - Date.UTC(+d.lunes.slice(0, 4), 0, 1)) / 86_400_000 / 7) + 1;

  return (
    <>
      <TituloPagina
        descripcion={`Se arma solo con las fechas de publicación y las fechas límite de cada entregable. Semana ${numeroSemana} · ${d.resumen.publicaciones} ${d.resumen.publicaciones === 1 ? "publicación" : "publicaciones"}, ${d.resumen.entregas} entregas, ${d.resumen.vencidas} vencidas.`}
        accion={
          <div className="flex items-center gap-1">
            <Boton variante="contorno" tamano="icono" aria-label="Semana anterior" onClick={() => setSemana(sumarDias(d.lunes, -7))}>
              <ChevronLeft />
            </Boton>
            <Boton variante="contorno" tamano="sm" className="h-11 md:h-8" onClick={() => setSemana(undefined)} disabled={d.hoy >= d.lunes && d.hoy <= d.domingo}>
              Hoy
            </Boton>
            <Boton variante="contorno" tamano="icono" aria-label="Semana siguiente" onClick={() => setSemana(sumarDias(d.lunes, 7))}>
              <ChevronRight />
            </Boton>
          </div>
        }
      >
        Calendario de campañas
      </TituloPagina>
      <p className="mb-3 font-titulo text-lg font-bold" aria-live="polite">
        {rango}
      </p>

      {d.campanas.length === 0 ? (
        <Vacio titulo="Semana sin fechas" texto="Ninguna campaña publica ni tiene fechas límite en estos días." />
      ) : (
        <>
          {/* Escritorio: rejilla campaña × día, con scroll propio. */}
          <div className="hidden overflow-x-auto border-y border-linea md:block">
            <table className="w-full min-w-[900px] table-fixed border-collapse font-ui text-sm">
              <thead>
                <tr className="border-b border-linea-fuerte">
                  <th scope="col" className="w-48 px-2 py-2 text-left text-[11px] font-bold uppercase tracking-wide text-tinta-tenue">
                    Campaña · publica
                  </th>
                  {d.dias.map((dia) => (
                    <th key={dia} scope="col" className={cn("px-2 py-2 text-left text-[11px] font-bold uppercase tracking-wide", dia === d.hoy ? "text-tinta" : "text-tinta-tenue")}>
                      {fechaCorta(dia)}
                      {dia === d.hoy && (
                        <>
                          {" "}
                          · hoy
                          <span className="ml-1 inline-block size-1.5 rounded-full bg-naranja align-middle" aria-hidden="true" />
                        </>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {d.campanas.map((c) => (
                  <tr key={c!.id} className="border-b border-linea align-top">
                    <th scope="row" className="px-2 py-3 text-left font-normal">
                      <Link to="/campanas/$id" params={{ id: c!.id }} className="font-semibold text-tinta hover:underline">
                        {c!.nombre}
                      </Link>
                      <p className="text-xs text-tinta-tenue">publica {fechaCorta(c!.fechaPublicacion)}</p>
                      <div className="mt-1">
                        <Sem semaforo={c!.semaforo} />
                      </div>
                    </th>
                    {d.dias.map((dia) => (
                      <td key={dia} className={cn("px-1.5 py-2", dia === d.hoy && "bg-fondo-suave")}>
                        <div className="grid gap-1">
                          {c!.publicaEnSemana && c!.fechaPublicacion === dia && (
                            <div className="border border-azul bg-azul/10 px-2 py-1 text-xs">
                              <p className="font-semibold text-azul">Publicación</p>
                              <p className="text-tinta-2">{c!.nombre}</p>
                            </div>
                          )}
                          {c!.chips
                            .filter((ch) => ch.dia === dia)
                            .map((ch) => (
                              <div
                                key={ch.tareaId}
                                className={cn(
                                  "border px-2 py-1 text-xs",
                                  ch.estado === "terminado" ? "border-linea text-tinta-tenue line-through" : ch.vencida ? "border-rojo bg-rojo/10" : "border-linea bg-fondo",
                                )}
                              >
                                <p className={cn("font-semibold", ch.vencida && ch.estado !== "terminado" ? "text-rojo" : "text-tinta")}>{ch.titulo}</p>
                                <p className="text-tinta-tenue">
                                  {ch.quien ?? "Sin responsable"}
                                  {vencioEl(ch) ? ` · venció ${fechaCorta(vencioEl(ch))}, sigue pendiente` : ch.estado === "terminado" ? " · entregado" : ""}
                                </p>
                              </div>
                            ))}
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Celular: por día. */}
          <div className="grid gap-4 md:hidden">
            {d.dias.map((dia) => {
              const items = d.campanas.flatMap((c) => [
                ...(c!.fechaPublicacion === dia ? [{ clave: `pub-${c!.id}`, titulo: `Publicación · ${c!.nombre}`, sub: null as string | null, tono: "azul" as const, campanaId: c!.id }] : []),
                ...c!.chips
                  .filter((ch) => ch.dia === dia)
                  .map((ch) => ({
                    clave: ch.tareaId,
                    titulo: ch.titulo,
                    sub: `${c!.nombre} · ${ch.quien ?? "Sin responsable"} · ${ETIQUETA_TAREA[ch.estado as EstadoTarea]}`,
                    tono: ch.vencida && ch.estado !== "terminado" ? ("rojo" as const) : ("neutro" as const),
                    campanaId: c!.id,
                  })),
              ]);
              if (items.length === 0) return null;
              return (
                <section key={dia}>
                  <h3 className={cn("mb-1 font-ui text-xs font-bold uppercase tracking-wide", dia === d.hoy ? "text-tinta" : "text-tinta-tenue")}>
                    {fechaCorta(dia)}
                    {dia === d.hoy && " · hoy"}
                  </h3>
                  <ul className="divide-y divide-linea border-y border-linea">
                    {items.map((it) => (
                      <li key={it.clave} className="py-2.5">
                        <Link to="/campanas/$id" params={{ id: it.campanaId }} className="block">
                          <p className={cn("font-ui text-sm font-semibold", it.tono === "rojo" ? "text-rojo" : it.tono === "azul" ? "text-azul" : "text-tinta")}>{it.titulo}</p>
                          {it.sub && <p className="font-ui text-xs text-tinta-tenue">{it.sub}</p>}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </section>
              );
            })}
          </div>
        </>
      )}

      <div className="mt-8 grid gap-8 md:grid-cols-2">
        <section>
          <EncabezadoSeccion>Próximas publicaciones</EncabezadoSeccion>
          {d.proximas.length === 0 && d.solicitudesSinCampana.length === 0 ? (
            <p className="font-ui text-sm text-tinta-tenue">Nada programado todavía.</p>
          ) : (
            <ul className="divide-y divide-linea border-y border-linea">
              {d.proximas.map((p) => (
                <li key={p.id} className="flex items-center gap-4 py-2 font-ui text-sm">
                  <span className="w-24 shrink-0 tabular-nums text-tinta-tenue">{fechaCorta(p.fechaPublicacion)}</span>
                  <Link to="/campanas/$id" params={{ id: p.id }} className="text-tinta hover:underline">
                    {p.nombre}
                  </Link>
                </li>
              ))}
              {d.solicitudesSinCampana.map((s) => (
                <li key={s.id} className="flex items-center gap-4 py-2 font-ui text-sm">
                  <span className="w-24 shrink-0 tabular-nums text-tinta-tenue">{s.fechaPublicacion ? fechaCorta(s.fechaPublicacion) : "por confirmar"}</span>
                  <Link to="/campanas/solicitudes/$id" params={{ id: s.id }} className="text-tinta hover:underline">
                    {s.titulo}
                  </Link>
                  <span className="text-xs text-tinta-tenue">{s.estado === "incompleta" ? "incompleta" : "sin campaña"}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
        <section>
          <EncabezadoSeccion>Cómo leerlo</EncabezadoSeccion>
          <Nota>
            Cada chip es una fecha límite; el azul es la publicación. Un chip rojo ya venció o pone en riesgo la publicación. Una solicitud sin campaña aparece en la lista hasta que tenga fechas límite. Dirección ve este mismo calendario en modo consulta.
          </Nota>
        </section>
      </div>
    </>
  );
}
